/**
 * ADLProgressBar
 * CCH Care — Phase 3 → Phase 5d → Late Doc UI surfacing (2026-05-14)
 *
 * Shows completion progress across all 9 ADL sections.
 * Displays as a horizontal bar with section indicators below.
 *
 * Phase 5:  Added Vitals as the 9th section (was 8).
 * Phase 5d: Added naFlags prop — N/A sections count as complete
 *           and render a distinct muted dot labelled "N/A".
 *
 * Changes (2026-05-14) — Late documentation flag surfacing:
 * - Each signature object is now inspected for `late_documentation`
 *   (set on adl_signatures rows by signatures.php when the section is
 *   signed during the grace window or after a supervisor reopen).
 * - Late-signed sections retain the green "signed" dot but get a small
 *   amber "!" overlay in the upper-right corner. Reviewers scanning
 *   the bar can see at a glance which sections were charted late.
 * - The bar's overall percentage and color are unchanged — late
 *   signatures still count as complete (they ARE complete, just late).
 * - Defensive on shape: handles signature values as either truthy
 *   objects with `late_documentation: 1` or bare booleans (older
 *   records.php response shapes). The optional-chain check below
 *   produces a non-late dot when the property is missing.
 */

import { COLORS } from '../../config/constants';

const SECTIONS = [
  { key: 'hygiene',       label: 'Hygiene',    icon: '🛁' },
  { key: 'meals',         label: 'Meals',      icon: '🍽️' },
  { key: 'repositioning', label: 'Reposition', icon: '🔄' },
  { key: 'fluid_intake',  label: 'Fluids',     icon: '💧' },
  { key: 'bowel',         label: 'Bowel',      icon: '📋' },
  { key: 'urination',     label: 'Urine',      icon: '🚽' },
  { key: 'skin_checks',   label: 'Skin',       icon: '👁️' },
  { key: 'safety_checks', label: 'Safety',     icon: '✅' },
  { key: 'vitals',        label: 'Vitals',     icon: '🩺' },
];

export default function ADLProgressBar({ signatures = {}, naFlags = {}, onSectionClick, compact = false }) {

  // A section is "complete" if it has a CNA signature OR is flagged N/A in the care plan
  function isSectionComplete(key) {
    return !!signatures[key] || naFlags[key]?.is_applicable === false;
  }

  function isSectionNA(key) {
    return naFlags[key]?.is_applicable === false;
  }

  // late_documentation may arrive as 1, '1', true, or be missing entirely
  // depending on how PDO and the response shape pass it through. The
  // double-bang normalizes everything to a boolean. Missing → false.
  function isSectionLate(key) {
    return !!signatures[key]?.late_documentation;
  }

  const signedCount = SECTIONS.filter(s => isSectionComplete(s.key)).length;
  const pct         = Math.round((signedCount / SECTIONS.length) * 100);

  const barColor = pct === 100
    ? COLORS.accent
    : pct >= 50
      ? COLORS.primary
      : COLORS.warning;

  return (
    <div style={{ background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, padding: compact ? '10px 16px' : '14px 16px' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
          ADL Progress
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>
          {signedCount}/{SECTIONS.length} complete
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        height: 6, background: COLORS.border, borderRadius: 999,
        overflow: 'hidden', marginBottom: compact ? 0 : 10,
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: barColor, borderRadius: 999,
          transition: 'width 0.4s ease',
        }} />
      </div>

      {/* Section dots — hidden in compact mode */}
      {!compact && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          {SECTIONS.map(section => {
            const signed = !!signatures[section.key];
            const isNA   = isSectionNA(section.key);
            const isLate = signed && isSectionLate(section.key);
            const done   = signed || isNA;

            return (
              <button
                key={section.key}
                onClick={() => onSectionClick?.(section.key)}
                title={isLate ? `${section.label} — charted late` : section.label}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 3, background: 'none', border: 'none', cursor: onSectionClick ? 'pointer' : 'default',
                  padding: '2px 4px',
                }}
              >
                {/* Dot wrapper is positioned so the late overlay can anchor to it */}
                <div style={{ position: 'relative' }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: signed ? COLORS.accent : COLORS.surface,
                    border: isNA
                      ? `2px dashed ${COLORS.textMuted}`
                      : signed
                        ? 'none'
                        : `2px solid ${COLORS.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: isNA ? 7 : 10,
                    color: signed ? '#fff' : COLORS.textMuted,
                    fontWeight: 700,
                    transition: 'background 0.2s',
                  }}>
                    {signed ? '✓' : isNA ? 'N/A' : ''}
                  </div>

                  {/* Late-documentation overlay — small amber pill in the
                      upper-right. Only renders for signed-late sections.
                      White ring blends it visually against any underlying
                      surface or dot color. */}
                  {isLate && (
                    <div
                      aria-label="Late documentation"
                      style={{
                        position: 'absolute',
                        top: -4, right: -6,
                        minWidth: 14, height: 14, padding: '0 3px',
                        borderRadius: 7,
                        background: COLORS.warning,
                        color: '#fff',
                        fontSize: 9, fontWeight: 800, lineHeight: '14px',
                        textAlign: 'center',
                        border: `1.5px solid ${COLORS.surface}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                      }}
                    >
                      !
                    </div>
                  )}
                </div>

                <span style={{
                  fontSize: 9,
                  color: signed ? (isLate ? COLORS.warning : COLORS.accent) : isNA ? COLORS.textMuted : COLORS.textLight,
                  fontWeight: done ? 700 : 400,
                  whiteSpace: 'nowrap',
                }}>
                  {section.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}